import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ChartBar, CurrencyDollar, FileText, Buildings, Calculator, ListChecks, Export, ArrowRight } from "@phosphor-icons/react";
import { exportCSV } from "@/hooks/useTableFeatures";

const reportTypes = [
  { id: "funding_summary", label: "Funding Summary", desc: "Funding requested, committed, and disbursed by client", icon: CurrencyDollar },
  { id: "frn_status", label: "FRN Status", desc: "All FRNs with lifecycle status across forms", icon: FileText },
  { id: "pia_report", label: "PIA Inquiry Status", desc: "Form 471 PIA review breakdown", icon: ListChecks },
  { id: "invoice_report", label: "Invoice & Disbursement", desc: "Form 472 (BEAR) and 474 (SPI) summary", icon: CurrencyDollar },
  { id: "c2_utilization", label: "C2 Budget Utilization", desc: "Category 2 budget caps vs commitments", icon: Calculator },
  { id: "client_detail", label: "Client Detail", desc: "Full funding detail for a specific client", icon: Buildings },
];

const PIE_COLORS = ["#002FA7", "#00B341", "#FFD100", "#FF2A00", "#6B7280", "#7C3AED", "#0891B2", "#D97706"];
const fmt = (n) => (!n && n !== 0) ? "—" : `$${Number(n).toLocaleString()}`;

export default function Reports() {
  const [selectedType, setSelectedType] = useState("");
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ funding_year: "", client_id: "", status: "", category: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    if (!selectedType) { toast.error("Select a report type"); return; }
    if (selectedType === "client_detail" && !filters.client_id) { toast.error("Select a client for this report"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/reports/generate", { report_type: selectedType, ...filters });
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate report");
    } finally { setLoading(false); }
  };

  const handleExport = () => {
    if (!report) return;
    const cols = report.columns.map((c) => ({
      label: c.label, key: c.key,
      accessor: c.format === "currency" ? (row) => row[c.key] : undefined,
    }));
    exportCSV(report.rows, cols, `${report.report_type || "report"}.csv`);
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const showBarChart = report && ["funding_summary", "c2_utilization", "client_detail"].includes(report.report_type) && report.chart_data?.length > 0;
  const showPieChart = report && ["frn_status", "pia_report"].includes(report.report_type) && report.chart_data?.length > 0;

  return (
    <div className="p-6" data-testid="reports-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Analytics</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Report Builder</h1>
        <p className="text-sm text-[#6B7280] mt-1">Generate custom funding summaries and E-Rate analytics</p>
      </div>

      {/* Report type selector */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border border-gray-200 mb-6">
        {reportTypes.map((rt) => {
          const Icon = rt.icon;
          const active = selectedType === rt.id;
          return (
            <button key={rt.id} data-testid={`report-type-${rt.id}`} onClick={() => { setSelectedType(rt.id); setReport(null); }}
              className={`p-4 text-left border-r border-b border-gray-200 last:border-r-0 transition-colors ${active ? "bg-[#002FA7]/5 border-b-2 border-b-[#002FA7]" : "bg-white hover:bg-gray-50"}`}>
              <Icon size={20} className={active ? "text-[#002FA7]" : "text-[#6B7280]"} weight={active ? "bold" : "regular"} />
              <p className={`text-xs font-semibold mt-2 ${active ? "text-[#002FA7]" : "text-[#1A1D20]"}`}>{rt.label}</p>
              <p className="text-[10px] text-[#6B7280] mt-0.5 leading-tight">{rt.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      {selectedType && (
        <div className="bg-white border border-gray-200 p-5 mb-6" data-testid="report-filters">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-1 block">Funding Year</label>
              <Select value={filters.funding_year || "all"} onValueChange={(v) => setF("funding_year", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-fy-filter" className="rounded-none w-[120px]"><SelectValue placeholder="All Years" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Years</SelectItem>
                  {["2020","2021","2022","2023","2024","2025","2026","2027"].map((y) => <SelectItem key={y} value={y}>FY{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(selectedType === "client_detail" || selectedType !== "c2_utilization") && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-1 block">Client {selectedType === "client_detail" ? "*" : ""}</label>
                <Select value={filters.client_id || "all"} onValueChange={(v) => setF("client_id", v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="report-client-filter" className="rounded-none w-[200px]"><SelectValue placeholder="All Clients" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-[200px]">
                    {selectedType !== "client_detail" && <SelectItem value="all">All Clients</SelectItem>}
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {["funding_summary"].includes(selectedType) && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-1 block">Category</label>
                <Select value={filters.category || "all"} onValueChange={(v) => setF("category", v === "all" ? "" : v)}>
                  <SelectTrigger className="rounded-none w-[130px]"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="all">All</SelectItem><SelectItem value="category_1">Cat 1</SelectItem><SelectItem value="category_2">Cat 2</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            {["frn_status", "invoice_report"].includes(selectedType) && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-1 block">Status</label>
                <Select value={filters.status || "all"} onValueChange={(v) => setF("status", v === "all" ? "" : v)}>
                  <SelectTrigger className="rounded-none w-[130px]"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="all">All</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="submitted">Submitted</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="committed">Committed</SelectItem><SelectItem value="denied">Denied</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <button data-testid="generate-report-btn" onClick={generate} disabled={loading}
              className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-6 py-2 text-sm flex items-center gap-2 transition-colors disabled:opacity-50">
              <ChartBar size={16} weight="bold" /> {loading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {report && (
        <div className="space-y-6" data-testid="report-results">
          {/* Title + Export */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{report.title}</h2>
              <p className="text-xs text-[#6B7280] mt-1">{report.rows.length} records</p>
            </div>
            <button data-testid="export-report-btn" onClick={handleExport} className="flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
              <Export size={14} /> Export CSV
            </button>
          </div>

          {/* Totals */}
          {report.totals && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-gray-200">
              {Object.entries(report.totals).map(([key, value], i) => (
                <div key={key} className="p-4 border-r border-gray-200 last:border-r-0 bg-white">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">{key.replace(/_/g, " ")}</span>
                  <p className="text-lg font-bold text-[#1A1D20] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {typeof value === "number" && key.includes("amount") || key.includes("requested") || key.includes("committed") || key.includes("disbursed") || key.includes("cap") || key.includes("invoiced") ? fmt(value) : value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Chart */}
          {showBarChart && (
            <div className="bg-white border border-gray-200 p-6" data-testid="report-chart">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-4">Visualization</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={report.chart_data} barCategoryGap="20%">
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v)} />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 12 }} />
                  {report.chart_data[0]?.requested !== undefined && <Bar dataKey="requested" fill="#002FA7" name="Requested" />}
                  {report.chart_data[0]?.committed !== undefined && <Bar dataKey="committed" fill="#00B341" name="Committed" />}
                  {report.chart_data[0]?.disbursed !== undefined && <Bar dataKey="disbursed" fill="#FFD100" name="Disbursed" />}
                  {report.chart_data[0]?.budget !== undefined && <Bar dataKey="budget" fill="#002FA7" name="Budget" />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {showPieChart && (
            <div className="bg-white border border-gray-200 p-6" data-testid="report-chart">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-4">Distribution</p>
              <div className="flex items-center gap-8">
                <ResponsiveContainer width={220} height={220}>
                  <PieChart>
                    <Pie data={report.chart_data} cx="50%" cy="50%" outerRadius={90} dataKey="value" labelLine={false}>
                      {report.chart_data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {report.chart_data.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-3 h-3 flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-sm text-[#1A1D20]">{item.name}</span>
                      <span className="text-sm font-semibold text-[#1A1D20] ml-auto">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {report.report_type === "invoice_report" && report.chart_data?.length > 0 && (
            <div className="bg-white border border-gray-200 p-6" data-testid="report-chart">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-4">Invoiced vs Disbursed</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={report.chart_data} layout="vertical" barCategoryGap="30%">
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => fmt(v)} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#6B7280" }} width={80} />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 12 }} />
                  <Bar dataKey="value" fill="#002FA7" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Data Table */}
          <div className="border border-gray-200 bg-white" data-testid="report-table">
            <Table>
              <TableHeader>
                <TableRow className="data-table-header">
                  {report.columns.map((col) => (
                    <TableHead key={col.key} className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">{col.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={report.columns.length} className="text-center py-8 text-[#6B7280] text-sm">No data matching filters</TableCell></TableRow>
                ) : report.rows.map((row, ri) => (
                  <TableRow key={ri} className="border-b border-gray-100 hover:bg-gray-50">
                    {report.columns.map((col) => (
                      <TableCell key={col.key} className="py-3 px-4 text-sm text-[#1A1D20]">
                        {col.format === "currency" ? fmt(row[col.key]) : col.key === "pia_status" ? (
                          <Badge className={`rounded-none text-[10px] border ${{"not_started":"bg-gray-100 text-[#6B7280]","in_review":"bg-[#002FA7]/10 text-[#002FA7]","info_requested":"bg-[#FFD100]/10 text-[#946B00]","approved":"bg-[#00B341]/10 text-[#00B341]","denied":"bg-[#FF2A00]/10 text-[#FF2A00]"}[row[col.key]] || "bg-gray-100 text-[#6B7280]"}`}>{row[col.key]}</Badge>
                        ) : col.key === "utilization" ? (
                          <div className="flex items-center gap-2">
                            <div className="w-12 h-1.5 bg-gray-100"><div className="h-full bg-[#002FA7]" style={{ width: `${Math.min(row[col.key], 100)}%` }} /></div>
                            <span className="text-xs">{row[col.key]}%</span>
                          </div>
                        ) : String(row[col.key] ?? "—")}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!selectedType && !report && (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <ChartBar size={32} className="mx-auto text-[#6B7280] mb-3" />
          <p className="text-sm font-medium text-[#1A1D20]">Select a report type above to get started</p>
          <p className="text-xs text-[#6B7280] mt-1">Generate custom funding summaries, FRN status reports, PIA tracking, and more</p>
        </div>
      )}
    </div>
  );
}
